import styled from '@emotion/styled'
import { PrebidModule, PrebidModuleDataBySite, PrebidModuleType } from '@pubstack/common/src/prebidModule'
import { FunctionComponent, useEffect, useState } from 'react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { Fonts } from '~/assets/style/fonts'
import Button from '~/components/Button'
import { CollapsiblePanel } from '~/components/CollapsiblePanel'
import { Link } from '~/components/Link'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { normalizeAsTrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Modal } from '~/components/Modal'
import { Spinner } from '~/components/Spinner'
import { Status } from '~/components/Status'
import { useToast } from '~/components/Toast/useToasts'
import { Toggle } from '~/components/Toggle'
import { Tooltip } from '~/components/Tooltip'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { JSON_SPACE_COUNT } from '~/types/utils'
import { PrebidModuleClassicForm } from './PrebidModuleClassicForm'
import { PrebidModuleJsonForm } from './PrebidModuleJsonForm'
import { variantHasValue } from './PrebidModuleParamInput'

const CollapsibleTitle = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
`

const CollapsibleToggle = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  width: 50%;
`

const CollapsibleContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const CollapisbleContentHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const Settings = styled.span`
  ${Fonts.H2};  
`

const NoRequiredSettings = styled.div`
  font-style: italic;
  ${Link} {
    font-style: normal;
  }
`

const ClassicMode = styled.div`
  margin-bottom: 20px;
`

const Actions = styled.div`
  display: inline-flex;
  gap: 20px;
  align-items: center;
  align-self: flex-end;
`

export type FormMode = 'Classic' | 'JSON'

export type ClassicFormPayload = { config: PrebidModuleDataBySite<PrebidModuleType> }

/**
 * The JSON mode exists to reach settings the catalog does not declare in formParams. The multiple edit flyout syncs
 * both forms on every keystroke, which a deep shape (a json, an objectList or a oneOf param) does not survive, so the
 * raw mode is hidden there. The per site form switches modes explicitly and handles those shapes.
 */
export const hidesJsonMode = (formParams: PrebidModule['formParams']): boolean => Object.values(formParams).some(({ type }) => type === 'json' || type === 'objectList' || type === 'oneOf')

export type CollapsiblePrebidModuleProps = {
  prebidModule: PrebidModule
  data: PrebidModuleDataBySite<PrebidModuleType>
  isLoading: boolean
  updateSiteData: (data: PrebidModuleDataBySite<PrebidModuleType>) => Promise<void>
  isForbidden?: boolean
}

type DisableGreenbidsAnalyticsModuleModalProps = {
  value: any
  onChange: (...event: any[]) => void
  onConfirm: (value: boolean) => void
}

const DisableGreenbidsAnalyticsModuleModal: FunctionComponent<DisableGreenbidsAnalyticsModuleModalProps> = ({ value, onChange, onConfirm }) => {
  const { close } = useGlobalModal()
  return (
    <Modal.Content>
      <Modal.Title>Greenbids rules</Modal.Title>
      <Modal.Body>The greenbids RTD module of this site won't be effective anymore.</Modal.Body>
      <Modal.Actions>
        <Button variant={'tertiary'} onClick={close} trackingLabel={'adm-integrations-modules-setting-save-greenbids-analytics-cancel'}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            onConfirm(value)
            close()
            onChange(!value)
          }}
          trackingLabel={'adm-integrations-modules-setting-save-greenbids-analytics-confirm'}
        >
          Confirm
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}

const BaseCollapsiblePrebidModule = ({ prebidModule, data, isLoading, updateSiteData, isForbidden }: CollapsiblePrebidModuleProps) => {
  const { code, formParams, type } = prebidModule
  /*
   * Whether this panel is read-only, for either of the two reasons it can be.
   *
   * `isForbidden` is the module's own rule — Greenbids RTD cannot be configured on a media whose
   * Greenbids analytics is off. The role is the other: which modules run on a media is scope-wide
   * delivery config, so a `user` reads it and an owner changes it. They disable the same controls, so
   * they are folded into one flag here rather than checked twice at every control below.
   */
  const { canEdit, disabledReason } = useScopePermissions()
  const isReadOnly = isForbidden || !canEdit
  const [formMode, setFormMode] = useState<FormMode>('Classic')
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [collapsibleState, setCollapsibleState] = useState<'open' | undefined>(undefined)
  const [requiredSettings, setRequiredSettings] = useState<boolean>(false)
  // bumped when the raw JSON is pushed back into the classic form, to remount the inputs on the new shape
  const [classicFormKey, setClassicFormKey] = useState<number>(0)
  const toast = useToast()

  const generateModeLink = () => {
    const otherMode = formMode === 'JSON' ? 'Classic' : 'JSON'
    return (
      <Button
        variant="tertiary"
        disabled={formMode === 'JSON' && !jsonIsValid}
        onClick={() => {
          switchFormMode(otherMode)
        }}
        trackingLabel={`adm-integrations-modules-setting-${normalizeAsTrackingLabel(otherMode)}-mode-apply`}
      >
        {otherMode} mode
      </Button>
    )
  }

  const {
    control,
    formState: { isDirty, errors, isValid },
    reset,
    setValue,
    unregister,
    trigger,
  } = useForm<{ config: PrebidModuleDataBySite<PrebidModuleType> }>({
    defaultValues: { config: data },
    mode: 'onChange',
  })

  const jsonForm = useForm<{ jsonValue: string }>({
    defaultValues: { jsonValue: JSON.stringify(data.moduleData, undefined, JSON_SPACE_COUNT) },
    mode: 'onChange',
  })

  const {
    control: jsonControl,
    formState: { errors: jsonErrors, isDirty: jsonDirty, isValid: jsonIsValid },
    reset: jsonReset,
  } = jsonForm

  const jsonValue = useWatch({ control: jsonControl, name: 'jsonValue' })

  const restore = () => {
    reset({ config: data })
    jsonReset({ jsonValue: JSON.stringify(data.moduleData, undefined, JSON_SPACE_COUNT) })
    setRequiredSettings(false)
    // the variant a oneOf is showing is the user's pick until its inputs remount, so restoring the values is not
    // enough: without this the panel keeps a branch the fetched config does not carry, with its fields empty
    setClassicFormKey((key) => key + 1)
  }

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect -- restore() resets both react-hook-form instances to the fetched data; these are external-store writes and cannot move into render
    restore()
  }, [data])

  const config = useWatch({ control, name: 'config' })

  const switchFormMode = (mode: FormMode) => {
    if (mode === 'Classic' && jsonDirty) {
      const newValue = jsonValue ? JSON.parse(jsonValue) : data.moduleData
      // chiplistinput, the json editor and the repeatable groups read their value at mount, and a oneOf keeps the
      // variant it was showing, so the raw edit only reaches them by resetting the values and remounting the form.
      // keepDefaultValues leaves isDirty comparing against the fetched config, so Restore and Validate stay enabled.
      reset({ config: { ...config, moduleData: newValue } }, { keepDefaultValues: true })
      setClassicFormKey((key) => key + 1)
    } else if (isDirty) {
      // a oneOf variant switch unregisters the branch being left, and react-hook-form prunes `moduleData` when that
      // empties it, which means no setting is set
      jsonReset({ jsonValue: JSON.stringify(config.moduleData ?? {}, undefined, JSON_SPACE_COUNT) })
    }
    setFormMode(mode)
  }

  const areSettingsRequired = () => {
    let dataToCheck = config.moduleData
    if (formMode === 'JSON' && jsonValue) {
      try {
        dataToCheck = JSON.parse(jsonValue)
      } catch {
        dataToCheck = data.moduleData
      }
    }
    // only the presence of a top level value is checked here; the content of nested objectList items is left to the
    // form validation, which walks the nested params
    return Object.keys(formParams).some((param) => {
      const [param1, param2] = param.split('.')
      const parent = dataToCheck?.[param1 as 'params' | 'storage' | 'options']
      const value = param2 === undefined ? parent : parent?.[param2]
      const definition = formParams[param]
      const { type, optional } = definition
      if (type === 'boolean' || optional) {
        return false
      }
      if (type === 'oneOf') {
        // a oneOf asks for one of its branches, whichever the user picked: none of them carrying a value is what
        // makes the setting missing. Enabling the module used to skip the check entirely and let `params.eids: []`
        // through, while the Validate button next to it refused the very same config.
        return !Object.values(definition.variants).some((variant) => variantHasValue(variant, value))
      }
      if (type === 'array' || type === 'objectList') {
        return !Array.isArray(value) || value.length === 0
      }
      if (type === 'json') {
        // a string here is text that did not parse, and an empty array/object configures nothing
        return typeof value === 'string' || !value || Object.keys(value).length === 0
      }
      return !value
    })
  }

  const onSubmit = async (payload: PrebidModuleDataBySite<PrebidModuleType>) => {
    if (!areSettingsRequired()) {
      await updateSiteData(payload)
    } else {
      toast.alert('Please fill all the required settings before validation.')
    }
  }

  const saveModule = async () => {
    /*
     * `isValid` already greys the button out, but it is read from the formState proxy and React Compiler is free to
     * skip the render that would refresh it, so it is a hint rather than a gate. `trigger()` answers from a
     * validation run of its own, and it is the same gate the multiple edit flyout applies — the two views refusing
     * the same config is the point.
     */
    if (formMode === 'Classic' && !(await trigger())) {
      toast.alert('Please fill all the required settings before validation.')
      return
    }
    setIsSaving(true)
    if (formMode === 'JSON') {
      await onSubmit({
        ...data,
        enabled: true,
        moduleData: jsonValue ? JSON.parse(jsonValue) : data.moduleData,
      })
    } else {
      await onSubmit({
        ...data,
        enabled: true,
        moduleData: config.moduleData ?? {},
      })
    }
    setIsSaving(false)
  }

  const switchModuleAvailability = async (value: boolean) => {
    // user tries to enable module but settings are required before
    if (!value && areSettingsRequired()) {
      setCollapsibleState('open')
      setRequiredSettings(true)
    } else if (!value !== data.enabled) {
      setIsSaving(true)
      await updateSiteData({
        // BVN 2024-10-14 - Like form validation bringing activation, here activation must bring form updates, otherwise we can activate modules without validating the config
        ...data,
        moduleData: formMode === 'JSON' ? (jsonValue ? JSON.parse(jsonValue) : data.moduleData) : config.moduleData,
        enabled: !value,
      })
      setIsSaving(false)
    }
    if (value) {
      setRequiredSettings(false)
      setCollapsibleState(undefined)
    }
  }

  const modal = useGlobalModal()
  return (
    <CollapsiblePanel
      title={
        <CollapsibleTitle>
          <CollapsibleToggle>
            {isSaving ? (
              <Spinner isColored={true} />
            ) : (
              <Controller
                control={control}
                name={'config.enabled'}
                render={({ field: { onChange, value } }) => (
                  <Toggle
                    value={value}
                    onClick={async (event) => {
                      event.stopPropagation()
                      event.preventDefault()
                      if (prebidModule.code === 'greenbids' && data.enabled) {
                        return modal.open(DisableGreenbidsAnalyticsModuleModal, { value, onChange: onChange, onConfirm: switchModuleAvailability })
                      } else {
                        switchModuleAvailability(value ?? false)
                        onChange(!value)
                      }
                    }}
                    trackingLabel={value ? 'adm-sites-stacks-modules-setting-enable-unset' : 'adm-sites-stacks-modules-setting-enable-set'}
                    disabled={isReadOnly}
                  ></Toggle>
                )}
              />
            )}
            {data.label}
          </CollapsibleToggle>
          <Status state={data.enabled ? 'active' : requiredSettings ? 'error' : 'inactive'}>{data.enabled ? 'Active' : requiredSettings ? 'Required settings' : 'Not used'}</Status>
        </CollapsibleTitle>
      }
      isCollapsible={!isLoading ? { trackingLabel: 'adm-integrations-modules-setting-collapse-apply' } : undefined}
      forceState={collapsibleState || (isLoading ? 'closed' : undefined)}
      hasBorderWhenOpened={true}
      key={data.siteId}
    >
      <CollapsibleContent>
        <CollapisbleContentHeader>
          <Settings>Settings</Settings>
          {generateModeLink()}
        </CollapisbleContentHeader>
        {formMode === 'Classic' ? (
          <ClassicMode>
            {Object.keys(formParams).length || type === 'RTD' ? (
              <PrebidModuleClassicForm key={classicFormKey} prebidModule={prebidModule} control={control} errors={errors} setValue={setValue} unregister={unregister} isForbidden={isReadOnly} />
            ) : (
              <NoRequiredSettings>
                <div>No required settings.</div>
                <div>
                  Add optional settings by using <Link label={'JSON mode'} onClick={() => switchFormMode('JSON')} trackingLabel={'adm-integrations-modules-setting-json-mode-forced-apply'} />.
                </div>
              </NoRequiredSettings>
            )}
          </ClassicMode>
        ) : code ? (
          <FormProvider {...jsonForm}>
            <PrebidModuleJsonForm control={jsonControl} errors={jsonErrors} isForbidden={isReadOnly} />
          </FormProvider>
        ) : null}
        <Actions>
          {(isDirty || jsonDirty) && (
            <Button onClick={restore} iconName={'restore'} variant={'tertiary'} trackingLabel={'adm-integrations-modules-setting-restore'}>
              Restore
            </Button>
          )}
          {/* The reason on the one control that would otherwise just be greyed with no account of why —
              the toggle and the fields above are disabled elements, which dispatch no hover of their own. */}
          <Tooltip title={disabledReason} disabled={canEdit}>
            <Button
              disabled={(!isDirty && !jsonDirty) || (formMode === 'JSON' && !jsonIsValid) || (formMode === 'Classic' && !isValid) || isSaving || isReadOnly}
              iconSpin={isSaving}
              iconName={isSaving ? 'pubstack' : undefined}
              onClick={async () => {
                saveModule()
              }}
              trackingLabel={'adm-integrations-modules-setting-save'}
            >
              Validate
            </Button>
          </Tooltip>
        </Actions>
      </CollapsibleContent>
    </CollapsiblePanel>
  )
}

export const CollapsiblePrebidModule = BaseCollapsiblePrebidModule
